import { useState, useEffect } from "react";
import { HasAccess, useHasAccess } from "@/components/common/has-access";
import { AlertTriangle, ChevronDown, ChevronRight, GripVertical, PackageX, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  TooltipProvider,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  UnmappedSession,
  UnmappedSessionGroup,
  UserBundleOption,
} from "@/pages/audit/types/unmapped-sessions";
import { useUserBundlesForMapping } from "../hooks/use-user-bundles-for-mapping";
import { useFindUserByContact } from "../hooks/use-find-user-by-contact";
import { useMapSessions } from "../hooks/use-map-sessions";
import { SessionMapping } from "../services/map-sessions";
import BundleUsaeForm from "@/features/users/ui/usage-form";
import { useQueryClient } from "@tanstack/react-query";
import { isSessionCompatibleWithBundle } from "../constants/bundle-event-types";
import { AuditDateRange, AuditTimestamp } from "./audit-timestamp";

// ─── Capacity-aware batch assignment ─────────────────────────────────────────

/**
 * Assigns all sessions to bundles respecting capacity and type compatibility.
 *
 * Algorithm:
 *  1. Sort sessions by start_time ASC (earlier sessions get priority).
 *  2. For each session find candidate bundles that:
 *     a. Have a compatible event type (session name matches bundle's allowed types).
 *     b. Cover the session's start_time within their date range.
 *     Candidates are sorted by end_date ASC (soonest-expiring first).
 *  3. Pick the first candidate with remaining capacity
 *     (total_session_quantity - actual_session_count - already assigned in
 *     this batch > 0).
 *  4. If no type-compatible bundle has capacity or covers the date, assign
 *     null → "No Bundle".
 */
function buildBatchAssignments(
  sessions: UnmappedSession[],
  bundles: UserBundleOption[]
): Record<string, string | null> {
  // How many sessions we've already assigned to each bundle in this batch
  const batchCount: Record<string, number> = {};

  const sorted = [...sessions].sort(
    (a, b) =>
      new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
  );

  const result: Record<string, string | null> = {};

  for (const session of sorted) {
    const t = new Date(session.start_time).getTime();

    const inRange = bundles
      .filter((b) => {
        const start = new Date(b.start_date).getTime();
        const end = new Date(b.end_date).getTime();
        return t >= start && t <= end;
      })
      .sort(
        (a, b) =>
          new Date(a.end_date).getTime() - new Date(b.end_date).getTime()
      );

    const candidates = inRange.filter((b) =>
      isSessionCompatibleWithBundle(session.name, b.bundle_name)
    );

    let assigned = false;
    for (const bundle of candidates) {
      const alreadyAssigned = batchCount[bundle.bundle_usage_id] ?? 0;
      const available =
        bundle.total_session_quantity -
        bundle.actual_session_count -
        alreadyAssigned;
      if (available > 0) {
        result[session.id] = bundle.bundle_usage_id;
        batchCount[bundle.bundle_usage_id] = alreadyAssigned + 1;
        assigned = true;
        break;
      }
    }

    if (!assigned) {
      result[session.id] = null;
    }
  }

  return result;
}

/** Available slots in a bundle, accounting for sessions already dropped there. */
function availableCapacity(
  bundle: UserBundleOption,
  currentlyAssigned: number
): number {
  return (
    bundle.total_session_quantity -
    bundle.actual_session_count -
    currentlyAssigned
  );
}

// ─── Draggable session row ────────────────────────────────────────────────────

interface SessionRowProps {
  session: UnmappedSession;
  onDragStart: (sessionId: string) => void;
}

function SessionRow({ session, onDragStart }: SessionRowProps) {
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        onDragStart(session.id);
      }}
      className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-grab active:cursor-grabbing group"
    >
      <GripVertical className="h-3.5 w-3.5 text-gray-300 group-hover:text-gray-400 shrink-0" />
      <AuditTimestamp value={session.start_time} className="text-xs text-gray-700" />
    </div>
  );
}

// ─── Bundle bucket accordion ─────────────────────────────────────────────────

interface BundleBucketProps {
  bundleId: string | null;
  bundle: UserBundleOption | undefined;
  sessions: UnmappedSession[];
  isDragOver: boolean;
  isDropBlocked: boolean; // true when bundle is full and a drag is in progress
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (bundleId: string | null) => void;
  onSessionDragStart: (sessionId: string) => void;
  onCreateBundle?: () => void; // only on the No Bundle bucket
}

function BundleBucket({
  bundleId,
  bundle,
  sessions,
  isDragOver,
  isDropBlocked,
  onDragOver,
  onDragLeave,
  onDrop,
  onSessionDragStart,
  onCreateBundle,
}: BundleBucketProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const isNoBundleBucket = bundleId === null;

  // capacity = total slots - already mapped externally - currently assigned here
  const capacity = bundle
    ? bundle.total_session_quantity -
      bundle.actual_session_count -
      sessions.length
    : null;
  const isFull = capacity !== null && capacity <= 0;

  // Group sessions by name, sorted by start_time within each group
  const byName = sessions.reduce<Record<string, UnmappedSession[]>>(
    (acc, s) => {
      (acc[s.name] ??= []).push(s);
      return acc;
    },
    {}
  );
  for (const arr of Object.values(byName)) {
    arr.sort(
      (a, b) =>
        new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );
  }

  return (
    <div
      onDragOver={(e) => {
        if (!isDropBlocked) onDragOver(e);
        // still call preventDefault so cursor feedback works
        e.preventDefault();
      }}
      onDragLeave={onDragLeave}
      onDrop={(e) => {
        e.preventDefault();
        if (!isDropBlocked) onDrop(bundleId);
      }}
      className={cn(
        "rounded-md border overflow-hidden transition-colors",
        isDropBlocked
          ? "border-red-200 opacity-60 cursor-not-allowed"
          : isDragOver
          ? "border-blue-400 bg-blue-50/40"
          : isNoBundleBucket
          ? "border-dashed border-gray-300"
          : isFull
          ? "border-orange-200"
          : "border-gray-200"
      )}
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded((v) => !v)}
        className={cn(
          "w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors",
          isNoBundleBucket
            ? "bg-amber-50 hover:bg-amber-100"
            : isFull
            ? "bg-orange-50 hover:bg-orange-100"
            : "bg-gray-50 hover:bg-gray-100"
        )}
      >
        <span className="flex items-center gap-2 text-xs font-medium truncate pr-2">
          {isNoBundleBucket ? (
            <span className="flex items-center gap-1.5 text-amber-700">
              <PackageX className="h-3.5 w-3.5 shrink-0" />
              No Bundle
            </span>
          ) : (
            <span className={cn("text-gray-800", isFull && "text-orange-700")}>
              {bundle?.bundle_name}
            </span>
          )}

          <Badge
            variant="outline"
            className={cn(
              "text-[10px] px-1.5 py-0 h-4",
              isNoBundleBucket
                ? "border-amber-300 text-amber-700"
                : isFull
                ? "border-orange-300 text-orange-700"
                : "text-gray-600"
            )}
          >
            {sessions.length}
          </Badge>

          {bundle && (
            <span className="text-muted-foreground font-normal">
              · {bundle.actual_session_count + sessions.length}/{bundle.total_session_quantity} slots used
              {isFull && (
                <span className="text-orange-600 font-medium"> · full</span>
              )}
              {!isFull && capacity !== null && (
                <span className="text-green-700 font-medium">
                  {" "}· {capacity} available
                </span>
              )}
              ·{" "}
              <AuditDateRange
                start={bundle.start_date}
                end={bundle.end_date}
                className="text-muted-foreground font-normal"
              />
            </span>
          )}
        </span>

        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
      </button>

      {/* Sessions */}
      {isExpanded && (
        <div className="divide-y divide-gray-100">
          {sessions.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground italic">
              {isFull ? "Bundle is full" : "Drop sessions here"}
            </p>
          ) : (
            Object.entries(byName).map(([name, nameSessions]) => {
              const isMismatch =
                !isNoBundleBucket &&
                bundle !== undefined &&
                !isSessionCompatibleWithBundle(name, bundle.bundle_name);
              return (
                <div key={name}>
                  <p
                    className={cn(
                      "px-3 pt-2 pb-0.5 text-[11px] font-semibold uppercase tracking-wide flex items-center gap-1",
                      isMismatch ? "text-amber-600" : "text-gray-500"
                    )}
                  >
                    {isMismatch && (
                      <AlertTriangle className="h-3 w-3 shrink-0 text-amber-500" />
                    )}
                    {name}
                  </p>
                  {nameSessions.map((s) => (
                    <SessionRow
                      key={s.id}
                      session={s}
                      onDragStart={onSessionDragStart}
                    />
                  ))}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Create bundle button — No Bundle bucket only */}
      {isNoBundleBucket && onCreateBundle && isExpanded && (
        <div className="px-3 py-2 border-t border-dashed border-gray-200">
          <HasAccess permission="write">
          <button
            onClick={onCreateBundle}
            className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 hover:underline transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Create new bundle
          </button>
          </HasAccess>
        </div>
      )}

      {/* Drop feedback when collapsed */}
      {!isExpanded && isDragOver && !isDropBlocked && (
        <p className="px-3 py-2 text-xs text-blue-600 text-center bg-blue-50/60">
          Drop here
        </p>
      )}
      {!isExpanded && isDragOver && isDropBlocked && (
        <p className="px-3 py-2 text-xs text-red-600 text-center bg-red-50/60">
          Bundle is full
        </p>
      )}
    </div>
  );
}

// ─── Per-group mapping panel (shown inside the dialog) ───────────────────────

interface GroupMappingPanelProps {
  group: UnmappedSessionGroup;
  assignments: Record<string, string | null>;
  onAssign: (sessionId: string, bundleId: string | null) => void;
  onUserClick: (userId: string) => void;
}

function GroupMappingPanel({
  group,
  assignments,
  onAssign,
  onUserClick,
}: GroupMappingPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverBucket, setDragOverBucket] = useState<string | "NO_BUNDLE" | null>(null);
  const [showCreateBundleDialog, setShowCreateBundleDialog] = useState(false);

  const queryClient = useQueryClient();

  const { data, isLoading, refetch: refetchBundles } = useUserBundlesForMapping({
    phone: group.invitee_phone,
    email: group.invitee_email,
  });

  // Resolve actual user ID from contact info for drawer + bundle creation
  const { data: resolvedUserId } = useFindUserByContact(
    group.invitee_phone,
    group.invitee_email,
  );

  const bundles = data?.bundles ?? [];

  // Capacity-aware batch auto-suggest once bundles load
  useEffect(() => {
    if (!data) return;
    const unassigned = group.sessions.filter(
      (s) => assignments[s.id] === undefined
    );
    if (unassigned.length === 0) return;
    const suggested = buildBatchAssignments(unassigned, bundles);
    for (const [sessionId, bundleId] of Object.entries(suggested)) {
      onAssign(sessionId, bundleId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleDrop = (targetBundleId: string | null) => {
    if (!draggingId) {
      setDragOverBucket(null);
      return;
    }

    if (targetBundleId !== null) {
      const bundle = bundles.find((b) => b.bundle_usage_id === targetBundleId);
      if (bundle) {
        // Count sessions already assigned to this bucket (excluding the one being dragged)
        const currentlyIn = group.sessions.filter(
          (s) => s.id !== draggingId && assignments[s.id] === targetBundleId
        ).length;
        if (availableCapacity(bundle, currentlyIn) <= 0) {
          // Reject — bundle is full
          setDraggingId(null);
          setDragOverBucket(null);
          return;
        }
      }
    }

    onAssign(draggingId, targetBundleId);
    setDraggingId(null);
    setDragOverBucket(null);
  };

  // Derive bucket contents from current assignments
  const sessionsByBucket = group.sessions.reduce<
    Record<string, UnmappedSession[]>
  >(
    (acc, s) => {
      const key = assignments[s.id] ?? "NO_BUNDLE";
      (acc[key] ??= []).push(s);
      return acc;
    },
    {}
  );

  const assignedCount = group.sessions.filter(
    (s) => assignments[s.id] != null
  ).length;
  const unassignedCount = group.sessions.length - assignedCount;

  // All bundle IDs that either have sessions or are available
  const bucketBundleIds = [
    ...new Set([
      ...bundles.map((b) => b.bundle_usage_id),
      ...Object.keys(sessionsByBucket).filter((k) => k !== "NO_BUNDLE"),
    ]),
  ];

  return (
    <>
    {/* Create New Bundle dialog */}
    {showCreateBundleDialog && resolvedUserId && (
      <Dialog open={showCreateBundleDialog} onOpenChange={setShowCreateBundleDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          <BundleUsaeForm
            userId={resolvedUserId}
            onClose={() => {
              setShowCreateBundleDialog(false);
              // Refetch bundles so the new one appears as a bucket, then re-assign No Bundle sessions
              refetchBundles().then(({ data: newData }) => {
                if (!newData) return;
                const newBundles = newData.bundles ?? [];
                const unassigned = group.sessions.filter(
                  (s) => assignments[s.id] == null || assignments[s.id] === null
                );
                if (unassigned.length === 0) return;
                const suggested = buildBatchAssignments(unassigned, newBundles);
                for (const [sessionId, bundleId] of Object.entries(suggested)) {
                  onAssign(sessionId, bundleId);
                }
              });
              queryClient.invalidateQueries({ queryKey: ["user-bundles-for-mapping"] });
            }}
          />
        </DialogContent>
      </Dialog>
    )}

    <div className="rounded-md border border-gray-200 overflow-hidden">
      {/* Group header */}
      <div className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-100">
        <div className="flex items-center gap-2 text-xs min-w-0 flex-1 pr-2">
          {/* Clickable name */}
          <button
            type="button"
            onClick={() => resolvedUserId && onUserClick(resolvedUserId)}
            disabled={!resolvedUserId}
            className={cn(
              "font-semibold text-gray-800 truncate",
              resolvedUserId && "hover:text-blue-600 hover:underline cursor-pointer",
              !resolvedUserId && "cursor-default"
            )}
          >
            {group.invitee_full_name ?? group.identity_key}
          </button>

          {/* Phone/email for manual reference */}
          {group.invitee_phone && (
            <span className="font-mono text-gray-500 shrink-0">{group.invitee_phone}</span>
          )}
          {!group.invitee_phone && group.invitee_email && (
            <span className="text-gray-500 shrink-0 truncate max-w-[160px]">{group.invitee_email}</span>
          )}

          <span className="text-muted-foreground font-normal shrink-0">
            · {group.session_count} session{group.session_count !== 1 ? "s" : ""}
          </span>
          {assignedCount > 0 && (
            <span className="font-normal text-green-700 shrink-0">· {assignedCount} assigned</span>
          )}
          {unassignedCount > 0 && (
            <span className="font-normal text-amber-600 shrink-0">· {unassignedCount} unassigned</span>
          )}
        </div>
        <button
          onClick={() => setIsExpanded((v) => !v)}
          className="shrink-0 p-0.5 rounded hover:bg-gray-200 transition-colors"
        >
          {isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </button>
      </div>

      {isExpanded && (
        <div className="p-3 space-y-2 bg-white">
          {isLoading ? (
            <p className="text-xs text-muted-foreground py-2">Loading bundles…</p>
          ) : (
            <>
              {/* No-Bundle bucket — always shown, includes Create New Bundle */}
              <BundleBucket
                bundleId={null}
                bundle={undefined}
                sessions={sessionsByBucket["NO_BUNDLE"] ?? []}
                isDragOver={dragOverBucket === "NO_BUNDLE"}
                isDropBlocked={false}
                onDragOver={(e) => { e.preventDefault(); setDragOverBucket("NO_BUNDLE"); }}
                onDragLeave={() => setDragOverBucket(null)}
                onDrop={handleDrop}
                onSessionDragStart={setDraggingId}
                onCreateBundle={resolvedUserId ? () => setShowCreateBundleDialog(true) : undefined}
              />

              {/* One bucket per bundle with discrepancy */}
              {bucketBundleIds.length === 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  No bundles with session count discrepancy found — manual intervention needed.
                </p>
              )}

              {bucketBundleIds.map((bid) => {
                const bundle = bundles.find((b) => b.bundle_usage_id === bid);
                const sessionsHere = sessionsByBucket[bid] ?? [];
                const blocked =
                  !!draggingId &&
                  draggingId !== undefined &&
                  !!bundle &&
                  availableCapacity(bundle, sessionsHere.filter(
                    (s) => s.id !== draggingId
                  ).length) <= 0;

                return (
                  <BundleBucket
                    key={bid}
                    bundleId={bid}
                    bundle={bundle}
                    sessions={sessionsHere}
                    isDragOver={dragOverBucket === bid}
                    isDropBlocked={blocked}
                    onDragOver={(e) => { e.preventDefault(); if (!blocked) setDragOverBucket(bid); }}
                    onDragLeave={() => setDragOverBucket(null)}
                    onDrop={handleDrop}
                    onSessionDragStart={setDraggingId}
                  />
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
    </>
  );
}

// ─── Main table ───────────────────────────────────────────────────────────────

interface Props {
  groups: UnmappedSessionGroup[];
  onUserClick: (userId: string) => void;
}

export function UnmappedSessionsTable({ groups, onUserClick }: Props) {
  // Selecting rows only leads to a fix, so view-only gets no checkbox column.
  const canWrite = useHasAccess("write");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  // session_id → bundle_usage_id | null
  const [assignments, setAssignments] = useState<Record<string, string | null>>({});

  const { mutate: mapSessions, isPending: isMapping } = useMapSessions();

  useEffect(() => {
    setSelectedIds(new Set());
  }, [groups]);

  const allSelected =
    groups.length > 0 && groups.every((g) => selectedIds.has(g.identity_key));
  const someSelected = selectedIds.size > 0;

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(groups.map((g) => g.identity_key)));
    }
  };

  const selectedGroups = groups.filter((g) => selectedIds.has(g.identity_key));

  const openConfirmDialog = () => {
    setAssignments({});
    setIsConfirmOpen(true);
  };

  const handleAssign = (sessionId: string, bundleId: string | null) => {
    setAssignments((prev) => ({ ...prev, [sessionId]: bundleId }));
  };

  const allSelectedSessions = selectedGroups.flatMap((g) => g.sessions);
  const mappableSessions = allSelectedSessions.filter(
    (s) => assignments[s.id] != null
  );
  const skippedCount = allSelectedSessions.length - mappableSessions.length;

  const handleConfirmMap = () => {
    const mappings: SessionMapping[] = mappableSessions.map((s) => ({
      session_id: s.id,
      bundle_usage_id: assignments[s.id]!,
    }));

    if (mappings.length === 0) {
      setIsConfirmOpen(false);
      return;
    }

    mapSessions(mappings, {
      onSuccess: () => {
        setIsConfirmOpen(false);
        setSelectedIds(new Set());
        setAssignments({});
      },
    });
  };

  return (
    <TooltipProvider delayDuration={200}>
    <>
      {someSelected && (
        <div className="flex items-center gap-3 px-2 py-2.5 mb-2 rounded-md bg-gray-50 border border-gray-200">
          <span className="text-xs font-medium text-gray-600">
            {selectedIds.size} selected
          </span>
          <button
            onClick={toggleAll}
            className="text-xs text-blue-600 hover:underline"
          >
            {allSelected ? "Deselect all" : "Select all"}
          </button>
          <div className="ml-auto">
            <HasAccess permission="write">
            <Button size="sm" onClick={openConfirmDialog} disabled={isMapping}>
              Map to Bundle
            </Button>
            </HasAccess>
          </div>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            {canWrite && (
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  disabled={groups.length === 0}
                />
              </TableHead>
            )}
            <TableHead>Contact</TableHead>
            <TableHead>Name</TableHead>
            <TableHead className="text-center">Sessions</TableHead>
            <TableHead>Date Range</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((group) => {
            const isSelected = selectedIds.has(group.identity_key);
            const dates = group.sessions.map((s) => new Date(s.start_time));
            const earliest = new Date(Math.min(...dates.map((d) => d.getTime())));
            const latest = new Date(Math.max(...dates.map((d) => d.getTime())));

            return (
              <TableRow
                key={group.identity_key}
                className={cn(isSelected && "bg-blue-50/50")}
              >
                {canWrite && (
                  <TableCell>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggle(group.identity_key)}
                    />
                  </TableCell>
                )}

                <TableCell>
                  <div className="flex flex-col gap-0.5">
                    {group.invitee_phone && (
                      <span className="text-xs font-mono text-gray-700">
                        {group.invitee_phone}
                      </span>
                    )}
                    {group.invitee_email && (
                      <span className="text-xs text-muted-foreground">
                        {group.invitee_email}
                      </span>
                    )}
                  </div>
                </TableCell>

                <TableCell className="text-sm font-medium text-gray-800">
                  {group.invitee_full_name ?? (
                    <span className="text-muted-foreground italic text-xs">Unknown</span>
                  )}
                </TableCell>

                <TableCell className="text-center">
                  <Badge variant="outline" className="text-xs">
                    {group.session_count}
                  </Badge>
                </TableCell>

                <TableCell className="text-xs text-muted-foreground">
                  {group.session_count === 1 ? (
                    <AuditTimestamp value={earliest} />
                  ) : (
                    <AuditDateRange start={earliest} end={latest} />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Map Sessions to Bundle</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Sessions are auto-assigned based on bundle date ranges. Drag any session
              into a different bundle to reassign it, or into{" "}
              <span className="font-medium text-amber-700">No Bundle</span> to skip it.
            </p>

            <div className="space-y-3">
              {selectedGroups.map((group) => (
                <GroupMappingPanel
                  key={group.identity_key}
                  group={group}
                  assignments={assignments}
                  onAssign={handleAssign}
                  onUserClick={onUserClick}
                />
              ))}
            </div>

            {/* Summary */}
            <div className="rounded-md border border-gray-200 px-3 py-2.5 bg-gray-50 space-y-1">
              <p className="text-xs text-gray-700">
                <span className="font-semibold text-gray-900">
                  {mappableSessions.length}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-900">
                  {allSelectedSessions.length}
                </span>{" "}
                session{allSelectedSessions.length !== 1 ? "s" : ""} will be mapped.
              </p>
              {skippedCount > 0 && (
                <p className="text-xs text-amber-700">
                  ⚠ {skippedCount} session{skippedCount !== 1 ? "s" : ""} in "No Bundle" will be skipped.
                </p>
              )}
              <p className="text-xs text-amber-700 font-medium">
                This action cannot be undone.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsConfirmOpen(false)}
              disabled={isMapping}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmMap}
              disabled={isMapping || mappableSessions.length === 0}
            >
              {isMapping
                ? "Mapping…"
                : `Map ${mappableSessions.length} Session${mappableSessions.length !== 1 ? "s" : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
    </TooltipProvider>
  );
}
